import { Card } from '@shared/ui/Card';
import { KsPackageSidebar } from '../../KsPackageSidebar';
import { KsPackageHeader } from '../../KsPackageHeader';
import { KsPackageActsTab } from '../../KsPackageActsTab';
import { KsPackageDocsTab } from '../../KsPackageDocsTab';
import { KsPackageLinesTab } from '../../KsPackageLinesTab';
import { KsPackageMaterialsTab } from '../../KsPackageMaterialsTab';
import { KsPackageIssuesTab } from '../../KsPackageIssuesTab';
import { KsPackageFormsTab } from '../../KsPackageFormsTab';
import { RegistryPager } from '../../registryPaging';
import { Err, Grid, Muted } from '../styles';
import { apiErr } from '../helpers';
import type { useKsPackageState } from '../hooks/useKsPackageState';

export function KsPackagesContent({ state }: { state: ReturnType<typeof useKsPackageState> }) {
  const {
    packages,
    packagesPage,
    packagesHasMore,
    packagesFetching,
    selectedId,
    setSelectedId,
    can_write,
    creating,
    contextContracts,
    contextProjects,
    estimateOptions,
    activeProjectId,
    setActiveProject,
    handleCreate,
    createError,
    detail,
    detailQError,
    pkg,
    acts,
    allLines,
    step,
    setStep,
    stepDone,
    stepBadge,
    frozen,
    busy,
    building,
    issuing,
    sendingRequest,
    canDelete,
    issues,
    kfoGateIssue,
    handleBuild,
    handleIssue,
    handleSendDsRequest,
    handleDelete,
    contractLsrs,
    rendering,
    renderingAct,
    savingAct,
    handleRenderAct,
    handleAddAct,
    actError,
    docs,
    importingMto,
    importingLogist,
    uploadingDocs,
    docError,
    handleImportFromMto,
    handleImportFromLogist,
    handleUploadDocs,
    lines,
    actFilter,
    setActFilter,
    issuesByLine,
    setNotice,
    handleBulkExclude,
    handleBulkDecision,
    materials,
    materialSummary,
    counts,
    annotating,
    handleDecision,
    forms,
    formsError,
    formsLoading,
    refreshingForms,
    handleRefreshForms,
    handleRender,
    handleExport,
    exporting,
    renderQueuedAt,
    renderWatchUntil,
    formActId,
    setFormActId,
    formCode,
    setFormCode,
    handleDownloadForm,
  } = state;

  return (
    <Grid>
      <KsPackageSidebar
        packages={packages}
        pager={
          <RegistryPager
            label="Пакетов"
            page={packagesPage}
            shown={packages.length}
            hasMore={packagesHasMore}
            fetching={packagesFetching}
          />
        }
        selectedId={selectedId}
        onSelectPackage={setSelectedId}
        canWrite={can_write}
        creating={creating}
        contextContracts={contextContracts}
        contextProjects={contextProjects}
        estimateOptions={estimateOptions}
        activeProjectId={activeProjectId}
        setActiveProject={setActiveProject}
        onCreatePackage={handleCreate}
        createError={createError}
      />

      <div>
        {!selectedId && <Muted>Выберите пакет слева или заведите новый.</Muted>}
        {selectedId && !detail && detailQError && (
          <Err>
            Карточка пакета не прочитана: {apiErr(detailQError, 'сервер не ответил')}. Выберите
            пакет ещё раз или обновите страницу.
          </Err>
        )}
        {selectedId && !detail && !detailQError && <Muted>Загрузка…</Muted>}
        {detail && pkg && (
          <Card>
            <KsPackageHeader
              pkg={pkg}
              actsCount={acts.length}
              allLinesCount={allLines.length}
              step={step}
              onSelectStep={setStep}
              stepDone={stepDone}
              stepBadge={stepBadge}
              canWrite={can_write}
              frozen={frozen}
              busy={busy}
              building={building}
              issuing={issuing}
              sendingRequest={sendingRequest}
              canDelete={canDelete}
              revisions={detail.revisions ?? null}
              hasOverContract={allLines.some((l) => l.over_contract)}
              hasIssues={issues.length > 0}
              kfoGateIssue={kfoGateIssue}
              onBuild={handleBuild}
              onIssue={handleIssue}
              onSendDsRequest={handleSendDsRequest}
              onDelete={handleDelete}
            />

            {step === 'setup' && (
              <KsPackageActsTab
                acts={acts}
                contractLsrs={contractLsrs}
                canWrite={can_write}
                frozen={frozen}
                busy={busy}
                rendering={rendering}
                renderingAct={renderingAct}
                savingAct={savingAct}
                onRenderAct={handleRenderAct}
                onAddAct={handleAddAct}
                actError={actError}
              />
            )}

            {step === 'build' && (
              <KsPackageDocsTab
                packageId={pkg.id}
                pkgStatus={pkg.status}
                docs={docs}
                acts={acts}
                canWrite={can_write}
                frozen={frozen}
                busy={busy}
                building={building}
                importingMto={importingMto}
                importingLogist={importingLogist}
                uploadingDocs={uploadingDocs}
                docError={docError}
                onBuild={handleBuild}
                onImportMto={handleImportFromMto}
                onImportLogist={handleImportFromLogist}
                onUploadDocs={handleUploadDocs}
              />
            )}

            {step === 'lines' && (
              <KsPackageLinesTab
                packageId={pkg.id}
                lines={lines}
                acts={acts}
                actFilter={actFilter}
                onActFilterChange={setActFilter}
                issuesByLine={issuesByLine}
                canWrite={can_write}
                frozen={frozen}
                onNotice={setNotice}
                onBulkExclude={handleBulkExclude}
                onBulkDecision={handleBulkDecision}
              />
            )}

            {step === 'materials' && (
              <KsPackageMaterialsTab
                materials={materials}
                materialSummary={materialSummary}
                acts={acts}
                actFilter={actFilter}
                onActFilterChange={setActFilter}
              />
            )}

            {step === 'issues' && (
              <KsPackageIssuesTab
                pkg={pkg}
                issues={issues}
                counts={counts}
                canWrite={can_write}
                frozen={frozen}
                annotating={annotating}
                onDecision={handleDecision}
              />
            )}

            {step === 'forms' && (
              <KsPackageFormsTab
                pkg={pkg}
                forms={forms}
                formsError={formsError}
                formsLoading={formsLoading}
                refreshingForms={refreshingForms}
                onRefreshForms={handleRefreshForms}
                onRender={handleRender}
                onExport={handleExport}
                rendering={rendering}
                exporting={exporting}
                canWrite={can_write}
                frozen={frozen}
                busy={busy}
                renderQueuedAt={renderQueuedAt}
                renderWatchUntil={renderWatchUntil}
                acts={acts}
                formActId={formActId}
                setFormActId={setFormActId}
                formCode={formCode}
                setFormCode={setFormCode}
                renderingAct={renderingAct}
                onRenderAct={handleRenderAct}
                onDownloadForm={handleDownloadForm}
                apiErr={apiErr}
              />
            )}
          </Card>
        )}
      </div>
    </Grid>
  );
}
